<script setup lang="ts">
import type { OnboardingPopupTaskRow } from '~/constants/onboarding-popup-tasks';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { navigateTo, useRoute } from '#app/composables/router';
import { LICENSE_LOCKED_ROUTE_NAME, useAuth, useMemberWorkspaces, useOnboardingPopup } from '#layers/access/public';
import { usePaneStore } from '#layers/ui/public';
import { useWorkspaceStore } from '#layers/workspace/public';
import { onboardingPopupTaskConfig } from '~/constants/onboarding-popup-tasks';
import { isGuestOrPublicRoute } from '~/utils/routes';
import { ONBOARDING_TRANSITION_DURATION } from './OnboardingPopup/constants';
import DesktopRows from './OnboardingPopup/DesktopRows.vue';
import Flyout from './OnboardingPopup/Flyout.vue';
import Header from './OnboardingPopup/Header.vue';
import InlineRows from './OnboardingPopup/InlineRows.vue';
import { useOnboardingPopupDrag } from './OnboardingPopup/use-onboarding-popup-drag';
import { useOnboardingPopupFlyout } from './OnboardingPopup/use-onboarding-popup-flyout';

const MAX_POPUP_WIDTH = 264;
const SIDEBAR_HORIZONTAL_PADDING = 24;
const POPUP_INLINE_START_OFFSET = '0.75rem';

const { t } = useI18n();
const route = useRoute();
const { loggedIn } = storeToRefs(useAuth());

const { state, taskStatuses, progress, doneCount, totalCount, dismiss, collapse, toggle } = useOnboardingPopup();
const { memberWorkspaces } = useMemberWorkspaces();
const { currentWorkspaceSlug } = storeToRefs(useWorkspaceStore());
const paneStore = usePaneStore();
const inWorkspaceSidebar = computed(() => route.meta.layout === 'workspace');
const breakpoints = useBreakpoints(breakpointsTailwind);
const isDesktop = breakpoints.greaterOrEqual('sm');

// Prefer the workspace the user is currently in; fall back to their first membership
// when the popup is shown outside a workspace (e.g. the org dashboard).
const workspaceSlug = computed(() => currentWorkspaceSlug.value ?? memberWorkspaces.value[0]?.apiName ?? null);

const visible = computed(() =>
	loggedIn.value
	&& !isGuestOrPublicRoute(route)
	&& route.meta.layout !== 'onboarding'
	&& route.name !== LICENSE_LOCKED_ROUTE_NAME
	&& state.value.status === 'active',
);

const collapsed = computed(() => state.value.collapsed);
const progressLabel = computed(() => t('onboarding_popup.progress', { done: doneCount.value, total: totalCount }));
const workspaceRequiredLabel = computed(() => t('onboarding_popup.workspace_must_exist'));
const rows = computed<OnboardingPopupTaskRow[]>(() => {
	const createdWorkspaceDone = taskStatuses.value.some(
		(task) => task.id === 'createdWorkspace' && task.done,
	);

	return taskStatuses.value.map((task) => {
		const config = onboardingPopupTaskConfig[task.id];
		const disabled = config.requiresCreatedWorkspace && (!createdWorkspaceDone || !workspaceSlug.value);

		return {
			id: task.id,
			label: t(config.labelKey),
			description: t(config.descriptionKey),
			actionLabel: t(config.actionKey),
			done: task.done,
			to: config.to(workspaceSlug.value),
			disabled,
		};
	});
});
const guideRef = useTemplateRef<HTMLElement>('guide');
const dragSurfaceRef = useTemplateRef<HTMLElement>('dragSurface');
const dragRegionRef = useTemplateRef<HTMLElement>('dragRegion');
const flyoutRef = useTemplateRef<HTMLElement>('flyout');
const flyoutPanelRef = useTemplateRef<InstanceType<typeof Flyout>>('flyoutPanel');
const {
	activeRow,
	close: closeFlyout,
	content: flyoutContent,
	events: flyoutEvents,
	highlightedRowId,
	open: flyoutOpen,
} = useOnboardingPopupFlyout({
	rows,
	enabled: () => visible.value && !collapsed.value && isDesktop.value,
	guideElement: guideRef,
	flyoutElement: flyoutRef,
	actionElement: () => flyoutPanelRef.value?.actionElement,
});
const {
	dragging,
	events: dragEvents,
	settling,
	style: dragStyle,
} = useOnboardingPopupDrag({
	collapsedElement: dragRegionRef,
	collapsed,
	duration: ONBOARDING_TRANSITION_DURATION,
	enabled: () => visible.value && !isDesktop.value,
	onCollapse,
	onExpand: toggle,
	surfaceElement: dragSurfaceRef,
});

const popupStyle = computed(() => {
	const width = inWorkspaceSidebar.value
		? Math.min(paneStore.sidebarSize - SIDEBAR_HORIZONTAL_PADDING, MAX_POPUP_WIDTH)
		: MAX_POPUP_WIDTH;

	return {
		'--onboarding-transition-duration': `${ONBOARDING_TRANSITION_DURATION}ms`,
		'--onboarding-collapse-duration': `${dragging.value ? 0 : ONBOARDING_TRANSITION_DURATION}ms`,
		'--onboarding-popup-inline-start': inWorkspaceSidebar.value
			? `calc(var(--spacebar-width) + ${POPUP_INLINE_START_OFFSET})`
			: POPUP_INLINE_START_OFFSET,
		'--onboarding-popup-width': `${width}px`,
	};
});

const dragSurfaceClass = computed(() => {
	if (dragging.value) return 'overflow-hidden transition-none will-change-[height]';

	if (settling.value) {
		return 'overflow-hidden motion-safe:transition-[height] motion-safe:duration-(--onboarding-transition-duration) motion-safe:ease-out';
	}

	return 'overflow-hidden';
});

function onCollapse() {
	closeFlyout();
	collapse();
}

function onDismiss() {
	closeFlyout();
	dismiss();
}

function onContentLeave(element: Element) {
	element.setAttribute('inert', '');
}

function onAction(row: OnboardingPopupTaskRow) {
	if (row.disabled) return;
	if (isDesktop.value) closeFlyout();
	else onCollapse();
	navigateTo(row.to);
}
</script>

<template>
	<Transition
		enter-active-class="motion-safe:transition motion-safe:duration-(--onboarding-transition-duration) ease-out"
		enter-from-class="scale-95 opacity-0 translate-y-2"
		enter-to-class="scale-100 opacity-100 translate-y-0"
		leave-active-class="motion-safe:transition motion-safe:duration-(--onboarding-transition-duration) ease-in"
		leave-from-class="scale-100 opacity-100 translate-y-0"
		leave-to-class="scale-95 opacity-0 translate-y-2"
	>
		<div
			v-if="visible"
			data-test="onboarding-popup"
			class="
				fixed inset-x-3 bottom-safe-offset-3 z-100000 w-auto origin-bottom-left
				sm:inset-x-auto sm:inset-s-(--onboarding-popup-inline-start) sm:bottom-3 sm:w-(--onboarding-popup-width)
				rtl:origin-bottom-right
			"
			:style="popupStyle"
		>
			<div
				ref="dragSurface"
				data-test="onboarding-drag-surface"
				class="
					max-h-[calc(100dvh-env(safe-area-inset-bottom)-1.5rem)] w-full rounded-lg bg-default shadow-lg ring ring-default
					sm:max-h-none sm:overflow-visible
				"
				:class="dragSurfaceClass"
				:style="dragStyle"
			>
				<DAnimatedSheet
					dimension="height"
					:duration="ONBOARDING_TRANSITION_DURATION"
					class="w-full"
					:ui="{
						root: !isDesktop
							? 'flex h-auto max-h-[inherit] w-full flex-col motion-safe:ease-out'
							: 'w-full motion-safe:ease-out',
						sheet: 'flex h-auto min-h-0 w-full flex-col sm:block sm:h-max',
					}"
				>
					<div
						ref="guide"
						:data-test="collapsed ? undefined : 'onboarding-expanded'"
						class="
							relative flex min-h-0 w-full touch-pan-x flex-col rounded-lg bg-default transition-colors select-none
							sm:touch-auto sm:select-auto
						"
						:class="collapsed ? 'p-3 hover:bg-elevated sm:px-2.5 sm:py-2' : 'p-3 sm:p-2.5'"
						@pointercancel="dragEvents.cancel"
						@pointerdown="dragEvents.start"
						@pointermove="dragEvents.move"
						@pointerup="dragEvents.end"
						@click.capture="dragEvents.captureClick"
					>
						<button
							v-if="collapsed"
							type="button"
							data-test="onboarding-collapsed"
							class="
								absolute inset-0 z-10 touch-pan-x rounded-lg select-none
								focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none
								sm:touch-auto sm:select-auto
							"
							:aria-label="$t('onboarding_popup.heading')"
							@click="dragEvents.clickCollapsed"
						/>

						<div
							ref="dragRegion"
							data-test="onboarding-drag-region"
							class="
								shrink-0 touch-pan-x select-none
								motion-safe:transition-[padding] motion-safe:duration-(--onboarding-collapse-duration) motion-safe:ease-out
								sm:touch-auto sm:transition-none sm:select-auto
							"
							:class="{ 'py-2 sm:py-0': !collapsed }"
						>
							<Header
								:collapsed
								:progress
								:progress-label
								@collapse="onCollapse"
								@dismiss="onDismiss"
							/>
						</div>

						<Transition
							:css="!dragging"
							enter-active-class="motion-safe:transition-[grid-template-rows] motion-safe:duration-(--onboarding-collapse-duration) motion-safe:ease-out sm:transition-none"
							enter-from-class="grid-rows-[0fr]"
							enter-to-class="grid-rows-[1fr]"
							leave-active-class="motion-safe:transition-[grid-template-rows] motion-safe:duration-(--onboarding-collapse-duration) motion-safe:ease-out sm:transition-none"
							leave-from-class="grid-rows-[1fr]"
							leave-to-class="grid-rows-[0fr]"
							@before-leave="onContentLeave"
						>
							<div
								v-if="!collapsed"
								class="grid min-h-0 touch-pan-y overflow-y-auto overscroll-contain sm:touch-auto sm:overflow-hidden"
							>
								<div class="min-h-0 min-w-0">
									<InlineRows
										:rows
										:workspace-required-label
										@pointerdown.stop
										@action="onAction"
									/>

									<div class="hidden sm:block">
										<DPopover
											:open="flyoutOpen"
											:content="flyoutContent"
											:reference="guideRef ?? undefined"
											:ui="{
												content: `
													hidden w-82 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg bg-default p-0 text-highlighted shadow-lg
													ring ring-default
													data-[state=closed]:animate-none
													data-[state=open]:animate-none
													sm:block
												`,
											}"
											@update:open="flyoutEvents.updateOpen"
										>
											<DesktopRows
												:rows
												:highlighted-row-id
												@row-hover="flyoutEvents.activateRow"
												@row-focus="flyoutEvents.selectRow"
												@row-click="flyoutEvents.clickRow"
												@pointerenter="flyoutEvents.scheduleOpen"
												@pointerleave="flyoutEvents.scheduleClose"
												@focusin="flyoutEvents.cancelClose"
												@focusout="flyoutEvents.scheduleClose"
												@mousemove="flyoutEvents.trackPointer"
											/>

											<template #content>
												<div
													v-if="activeRow"
													ref="flyout"
													data-test="onboarding-flyout"
													class="w-82"
													@pointerenter="flyoutEvents.cancelClose"
													@pointerleave="flyoutEvents.scheduleClose"
													@focusin="flyoutEvents.cancelClose"
													@focusout="flyoutEvents.scheduleClose"
													@mousemove="flyoutEvents.trackPointer"
												>
													<Flyout
														ref="flyoutPanel"
														:row="activeRow"
														:workspace-required-label
														@action="onAction(activeRow)"
													/>
												</div>
											</template>
										</DPopover>
									</div>
								</div>
							</div>
						</Transition>
					</div>
				</DAnimatedSheet>
			</div>
		</div>
	</Transition>
</template>
